/**
 * Runtime checks for incoming command params. Commands arrive as JSON, so the
 * types in `types.ts` describe what should arrive, not what does. These throw
 * on bad input; the router catches and logs.
 */

export const BASES = ["GENPOP", "FAMILIAR", "ADRECALL"];
export const STAT_TESTS = ["NONE", "POP", "YOY", "PTP"];
export const MODES = ["POINT_IN_TIME", "TREND", "PRE_POST"];
export const INTERVALS = ["P_W", "P_M", "P_Q", "MV_1", "MV_2", "MV_4", "MV_8", "MV_12"];
export const GROUPINGS = ["BRAND", "SEGMENT", "MARKET"];
export const TIME_PERIODS = ["YTD", "L180D", "L90D", "L30D", "L7D", "DRR", "CUSTOM"];
export const MODULES = ["equity", "conversion", "visibility", "advertising", "emotional", "reputation", "reports"];
export const SCREENS = ["brand", "segment", "market"];
export const SERIES_TYPES = ["brand", "market", "segment"];
export const EXPORT_FORMATS = ["pptx", "xlsx"];
export const CHART_TYPES = [
  "BAR", "LINE", "TABLE", "DONUT", "FUNNEL", "BUBBLE", "BARCODE",
  "DISTRIBUTION", "PERCEPTUAL", "BUTTERFLY", "DIFFERENTIATION",
];

export function oneOf(value: unknown, allowed: string[], field: string): string {
  const s = String(value);
  if (!allowed.includes(s)) {
    throw new Error(`${field} must be one of: ${allowed.join(", ")} — got "${s}"`);
  }
  return s;
}

/**
 * Brand ids are numbers, brand set ids are ULID strings, and the app tells them
 * apart by `typeof id === "string"`. So a brand id that arrives as "884" has to
 * become 884 here, or it would be queried as a brand set and return nothing.
 *
 * Same rule the URL parser uses (utils/url.ts): all-digits is a brand,
 * anything else is a brand set.
 */
export function ids(value: unknown, field: string): (number | string)[] {
  if (!Array.isArray(value) || value.length === 0) {
    throw new Error(`${field} must be a non-empty array`);
  }
  return value.map((v, i) => {
    // Coercion first would make anything an id: `null` becomes "null" and
    // `{}` becomes "[object Object]", both of which look like brand-set
    // ULIDs and fail silently three layers down. Only a string or a finite
    // number is an id.
    if (typeof v === "number") {
      if (!Number.isFinite(v)) throw new Error(`${field}[${i}] is not a number`);
      return Number.isInteger(v) ? v : String(v);
    }
    if (typeof v !== "string") throw new Error(`${field}[${i}] is not an id`);
    const s = v.trim();
    if (!s) throw new Error(`${field}[${i}] is empty`);
    const n = Number(s);
    return Number.isInteger(n) ? n : s;
  });
}

/** Market ids are always numeric, so "3" is accepted and becomes 3. */
export function marketIds(value: unknown, field: string): number[] {
  return ids(value, field).map((v) => {
    const n = Number(v);
    if (!Number.isInteger(n)) {
      throw new Error(`${field} must contain market ids (numbers) — got "${v}"`);
    }
    return n;
  });
}

/** Segment, filter and option codes are ULIDs — never numeric-coerced. */
export function codes(value: unknown, field: string): string[] {
  if (!Array.isArray(value) || value.length === 0) {
    throw new Error(`${field} must be a non-empty array`);
  }
  return value.map((v, i) => {
    const code = String(v).trim();
    // Same rule `ids` applies: a blank is not a code, and one written into the
    // selection is a segment or filter nothing can ever resolve.
    if (!code) throw new Error(`${field}[${i}] is empty`);
    return code;
  });
}

/** Like `codes`, but an empty list is a clear rather than an error. */
export function codesOrEmpty(value: unknown, field: string): string[] {
  if (Array.isArray(value) && value.length === 0) return [];
  return codes(value, field);
}

export function list(value: unknown, field: string): unknown[] {
  if (!Array.isArray(value)) throw new Error(`${field} must be an array`);
  return value;
}

export function text(value: unknown, field: string): string {
  if (typeof value !== "string" || !value) throw new Error(`${field} is required`);
  return value;
}

export function num(value: unknown, field: string): number {
  const n = Number(value);
  if (!Number.isFinite(n)) throw new Error(`${field} must be a number`);
  return n;
}

export function obj(value: unknown, field: string): Record<string, unknown> {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    throw new Error(`${field} must be an object`);
  }
  return value as Record<string, unknown>;
}

/** Named presets carry no dates; CUSTOM must carry both. */
export function period(p: Record<string, unknown>, field = "preset"): {
  preset: string;
  start?: string;
  end?: string;
} {
  const preset = oneOf(p.preset, TIME_PERIODS, field);
  if (preset !== "CUSTOM") return { preset };
  if (!p.start || !p.end) {
    throw new Error("start and end are required when preset is CUSTOM");
  }
  return { preset, start: String(p.start), end: String(p.end) };
}

/**
 * Filter selections. Codes are ULIDs on both the filter and its options, so
 * they are checked but never coerced.
 */
export function filters(value: unknown, field: string): unknown[] {
  return list(value, field).map((raw, i) => {
    const f = obj(raw, `${field}[${i}]`);
    const code = text(f.code, `${field}[${i}].code`);
    const kind = oneOf(f.kind, ["OPTION", "RANGE"], `${field}[${i}].kind`);
    if (kind === "OPTION") {
      return { code, kind, options: codes(f.options, `${field}[${i}].options`) };
    }
    const r = obj(f.range, `${field}[${i}].range`);
    return {
      code,
      kind,
      range: {
        min: num(r.min, `${field}[${i}].range.min`),
        max: num(r.max, `${field}[${i}].range.max`),
      },
    };
  });
}
